import { Grid, Stack, useTheme, Button, Divider } from '@mui/material';
import { useScreenSize } from '@/hooks/use-screen-size';
import { ListDetailView } from '@/components/common/list-detail-view';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { Title } from './title';
import UpdateFlowForm from './update-flow.form';
import { Moderators } from '../moderators/moderator-widget';
import type { UpdateFlowDto } from '@/types/dtos/flows/request/update-flow.dto';
import { useNotifications } from '@/hooks/use-notifications';
import useUpdateFlowMutation from '@/state/mutations/flows/use-update-flow';
import { useTranslations } from '@/i18n/use-translations';
import { useNavigate } from 'react-router';
import { MediaWidget } from '@/components/events/detail/tabs/common/media/media.widget.tsx';
import { getObjectId } from '@/helpers/object.id.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import { useMemo, useEffect, useRef, useState } from 'react';
import { FlowModeSection } from '@/components/flows/detail/flow-mode-section';
import Widget from '@/components/common/widget';
import { GET_FLOW_QUERY_KEY, useGetFlowQuery } from '@/state/queries/flows/use-get-flow';
import type { Mode } from '@/types/entity-mode';
import { useModeStore } from '@/state/stores/use-mode-store';
import { toFlowUiMode } from '@/components/flows/detail/normalize-flow-plan-view';
import { masterFlowsForEventList } from '@/helpers/flow-scope.helper';

type FlowDetailContentProps = {
  flow: FlowDto;
  updateFlow: (flow: UpdateFlowDto) => Promise<void>;
  updatePending: boolean;
};

const FlowDetailContent = ({ flow: flowProp, updateFlow, updatePending }: FlowDetailContentProps) => {
  const t = useTranslations();

  // Get flow origin ID for fetching modes
  const flowOriginId = flowProp?.origin ? getObjectId(flowProp.origin) : flowProp.id;

  const { flows: fetchedFlows = [] } = useGetFlowQuery(flowOriginId, undefined, {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    syncToStore: false,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000,
  });

  const planFlow = useMemo(
    () => fetchedFlows.find((flow) => flow.mode === 'PLAN') ?? (flowProp.mode === 'PLAN' ? flowProp : undefined),
    [fetchedFlows, flowProp],
  );
  const organizeFlow = useMemo(
    () =>
      fetchedFlows.find((flow) => flow.mode === 'ORGANIZE' || flow.mode === 'PERFORM') ??
      (flowProp.mode === 'ORGANIZE' || flowProp.mode === 'PERFORM' ? flowProp : undefined),
    [fetchedFlows, flowProp],
  );

  const setMode = useModeStore((s) => s.setMode);
  const storedMode = useModeStore((s) => s.flowMode[flowOriginId]);

  const computedMode = useMemo(() => {
    if (storedMode === 'PLAN' && planFlow) return storedMode;
    if ((storedMode === 'ORGANIZE' || storedMode === 'PERFORM') && organizeFlow) return storedMode;
    return planFlow ? 'PLAN' : organizeFlow ? 'ORGANIZE' : toFlowUiMode(flowProp.mode);
  }, [storedMode, planFlow, organizeFlow, flowProp.mode]);

  const [currentMode, setCurrentMode] = useState<Mode>(computedMode);
  const previousComputedModeRef = useRef<Mode>(computedMode);
  const userInitiatedChangeRef = useRef(false);

  // Sync computedMode to currentMode when underlying data or stored mode changes
  useEffect(() => {
    // If user manually changed mode and stored mode matches, don't override
    if (userInitiatedChangeRef.current && computedMode === previousComputedModeRef.current) {
      return;
    }

    // Reset user-initiated flag if computed mode changed from external data
    if (computedMode !== previousComputedModeRef.current) {
      userInitiatedChangeRef.current = false;
      queueMicrotask(() => setCurrentMode(computedMode));
    }

    previousComputedModeRef.current = computedMode;
  }, [computedMode]);

  const handleModeChange = (newMode: Mode) => {
    if (newMode === 'PLAN' && !planFlow) return;
    if ((newMode === 'ORGANIZE' || newMode === 'PERFORM') && !organizeFlow) return;
    userInitiatedChangeRef.current = true;
    setCurrentMode(newMode);
    if (flowOriginId) {
      const flowMode = useModeStore.getState().flowMode;
      setMode({
        flowMode: {
          ...flowMode,
          [flowOriginId]: newMode,
        },
      });
    }
  };

  const hasPlanMode = !!planFlow;
  const hasOrganizeMode = !!organizeFlow;
  const flowToUse = currentMode === 'PLAN' ? (planFlow ?? flowProp) : (organizeFlow ?? flowProp);
  const flowToDisplay = flowToUse ?? flowProp;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Stack spacing={2}>
          {/* Flow Mode Section */}
          <Widget title={t('flows.mode', { defaultValue: 'BRITESTAGE Process' })} sx={{ borderRadius: 0 }}>
            <FlowModeSection
              currentMode={currentMode}
              hasPlanMode={hasPlanMode}
              hasOrganizeMode={hasOrganizeMode}
              handleModeChange={handleModeChange}
              flow={flowToUse}
              planFlowOverride={planFlow}
              eventSlotIdParam={null}
              context="flowGrid"
              showDivider={false}
            />
          </Widget>
          {/* Flow Basics Widget */}
          <UpdateFlowForm
            flow={flowToDisplay}
            updatePending={updatePending}
            updateFlow={updateFlow}
            modeOverride={toFlowUiMode(flowToDisplay.mode)}
          />
          {/* Horizontal divider - only visible on small screens when columns stack */}
          <Divider orientation="horizontal" sx={{ display: { xs: 'block', lg: 'none' } }} />
        </Stack>
      </Grid>
      <Grid container direction="column" size={{ xs: 12, lg: 6 }} spacing={2}>
        <Moderators flow={flowToDisplay} />
        <MediaWidget
          media={flowToDisplay.media}
          parentId={flowToDisplay?.origin ? getObjectId(flowToDisplay.origin) : flowToDisplay.id}
          liveReady={flowToDisplay?.liveContext?.isRunning ?? false}
          parentType="Flow"
          context="Flow"
          enablePlayerSocket={false}
        />
      </Grid>
    </Grid>
  );
};

export const FlowGrid = ({ flows }: { flows: FlowDto[] }) => {
  const planFlows = useMemo(() => masterFlowsForEventList(flows).filter((flow) => flow.mode === 'PLAN'), [flows]);
  const theme = useTheme();
  const t = useTranslations();
  const navigate = useNavigate();
  const notification = useNotifications();
  const event = useEventStore((s) => s.event);
  const { isWideScreen } = useScreenSize({ breakpoint: 860 });
  const { mutateAsync, isPending: updatePending } = useUpdateFlowMutation();
  const eventId = event?.origin ? (typeof event.origin === 'string' ? event.origin : event.origin.id) : event?.id;

  const renderFlowListItem = (flow: FlowDto, isSelected: boolean) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        'p': 1,
        'border': 1,
        'borderColor': isSelected ? 'primary.main' : 'divider',
        'borderRadius': 2,
        'boxShadow': 1,
        'transition': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        'backgroundColor': isSelected
          ? 'action.selected'
          : theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
        'mb': 0.5,
        '&:hover': {
          boxShadow: 2,
          backgroundColor: isSelected ? 'action.selected' : 'action.hover',
        },
      }}
    >
      <Title flow={flow} />
    </Stack>
  );

  const renderFlowDetail = (flow: FlowDto) => {
    const flowOriginId = flow?.origin ? getObjectId(flow.origin) : flow.id;
    const updateFlow = async (dto: UpdateFlowDto) => {
      try {
        await mutateAsync({
          ...dto,
          flowQueryKey: [GET_FLOW_QUERY_KEY, flowOriginId, undefined],
        });
        notification.notifySuccess(t('flows.notification.updateSuccess', { title: dto.title ?? '' }));
      } catch {
        notification.notifyError(t('flows.notification.updateError'));
      }
    };
    return (
      <Stack spacing={2} sx={{ mt: isWideScreen ? 0 : 1 }}>
        <FlowDetailContent flow={flow} updateFlow={updateFlow} updatePending={updatePending} />
      </Stack>
    );
  };

  const renderHeaderActions = (flow: FlowDto) => {
    return (
      <Button
        color="info"
        size="small"
        variant="contained"
        onClick={(e) => {
          e.stopPropagation();
          const flowId = flow?.origin ? getObjectId(flow.origin) : flow.id;
          if (eventId) {
            navigate(`/flow/${flowId}?contextType=event&contextId=${eventId}`);
          } else {
            navigate(`/flow/${flowId}`);
          }
        }}
        sx={{
          minWidth: 'unset',
          padding: '2px 8px',
        }}
      >
        {t('common.studio')}
      </Button>
    );
  };

  return (
    <ListDetailView
      items={planFlows}
      renderListItem={renderFlowListItem}
      renderDetail={renderFlowDetail}
      getItemKey={(flow) => flow.id}
      getItemTitle={(flow) => flow.title}
      itemSubtitle={t('flows.subtitle')}
      listGridSize={{ xs: 12, md: 6, lg: 4 }}
      detailGridSize={{ xs: 12, md: 6, lg: 8 }}
      headerActions={renderHeaderActions}
    />
  );
};
